import { observer } from 'mobx-react';
import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import {
  Card,
  ErrorMessage,
  FlexRow,
  Footer,
  PageLoading,
  SaveDialog,
  Spinner,
  SpinnerSize,
  WorkflowHeader,
} from 'ui-toolkit';
import ReportNavBar from '../components/ReportNavBar';
import { SearchReportHeaderButtons } from '../components/SearchReportHeaderButtons/SearchReportHeaderButtons';
import { BankruptcyInformation } from '../forms/SearchReport/BankruptcyInformation';
import { DeedInformation } from '../forms/SearchReport/DeedInformation';
import { JudgementInformation } from '../forms/SearchReport/JudgementInformation';
import { LegalAndVesting } from '../forms/SearchReport/LegalAndVesting';
import { MortgageInformation } from '../forms/SearchReport/MortgageInformation';
import { PropertyTaxSummary } from '../forms/SearchReport/PropertyTaxSummary';
import { Summary } from '../forms/SearchReport/Summary';
import { RootStore } from '../services/RootStore';
import { TitleEvidence } from '../forms/SearchReport/TitleEvidence';
import { IRouterParams } from './IRouterParams';
import { clientUrls } from '../../ui-toolkit/rest/ClientUrlFactory';
import { Copyright } from '../../ui-toolkit/components/Copyright/Copyright';
import { eUSAState } from 'ui-toolkit/enums/states';
import { eAccessControlUserType } from 'ui-toolkit/enums/eAccessControlUserType';
import { createDefaultBankruptcyData } from '../../ui-toolkit/orders/interfaces/titleReport/IBankruptcy';
import { eTitleEvidenceFeeType } from '../../ui-toolkit/orders/interfaces/titleReport/eTitleEvidenceFeeType';
import { IOrderDetailsData } from '../../ui-toolkit/orders/interfaces/IOrderDetailsData';
import { upperFirst } from 'lodash';
import { OrderApi, useGetOrderQuery } from '../../API/order/OrderApi';
import { eAPITags } from '../../API/eAPITags';
import { useAppDispatch } from '../../store/store';

export interface ISearchReportProps extends RouteComponentProps<IRouterParams> {
  rootStore: RootStore;
  order?: IOrderDetailsData;
  userType: eAccessControlUserType;
}

const SearchReport: React.FC<ISearchReportProps> = ({ rootStore, order, userType, match, history }) => {
  const [goToNextPage, setGoToNextPage] = useState<(() => void) | undefined>(undefined);
  const [showSaveDialog, setShowSaveDialog] = useState<boolean>(false);

  const { data: orderData } = useGetOrderQuery(match.params.orderId);
  const dispatch = useAppDispatch();

  const orderId = match.params.orderId;

  const lastRefreshedOrderId = useRef<string | null>(null);
  const [isTitleCommitmentCreated, setIsTitleCommitmentCreated] = useState(false);

  // First useEffect: Initialize the title commitment data
  useEffect(() => {
    const initializeTitleCommitment = async () => {
      rootStore.appData!.setTitle(match.params.action);
      await rootStore.searchReport!.init(orderId);
      rootStore.searchReport!.initValidator(!!(order && order.property.state === eUSAState.TX));
      setIsTitleCommitmentCreated(true);
    };

    setIsTitleCommitmentCreated(false);
    initializeTitleCommitment().catch((error) => {
      console.error('Error initializing search report:', error);
    });
  }, [rootStore, match.params.action, orderId, order]);

  // Second useEffect: Handle refresh order for title commitment created
  useEffect(() => {
    if (
      isTitleCommitmentCreated &&
      !orderData?.transaction?.titleCommitmentId &&
      lastRefreshedOrderId.current !== orderId
    ) {
      dispatch(OrderApi.util.invalidateTags([{ type: eAPITags.OrderData, id: orderId }]));
      lastRefreshedOrderId.current = orderId;
    }
  }, [isTitleCommitmentCreated, orderData?.transaction?.titleCommitmentId, dispatch, orderId]);

  const getTitle = useCallback(() => {
    const title = upperFirst(rootStore.appData!.title.replace('new', 'create'));
    return `${title} Search Report & Title Commitment`;
  }, [rootStore.appData]);

  const safeGoToNextPage = useCallback(
    (to: string) => {
      const goToNextPageFn = () => {
        history.push(to);
      };

      if (rootStore.searchReport!.formDataChanged) {
        setGoToNextPage(() => goToNextPageFn);
        setShowSaveDialog(true);
      } else {
        setGoToNextPage(undefined);
        goToNextPageFn();
      }
    },
    [rootStore.searchReport, history]
  );

  const onFinalizeHandler = useCallback(async () => {
    await rootStore.searchReport!.finalize(match.params.orderId);
    const nextPage =
      userType === eAccessControlUserType.Admin
        ? clientUrls.urlFor.documentsIndex(match.params.orderId)
        : clientUrls.urlFor.vendorOrder(match.params.orderId);
    safeGoToNextPage(nextPage);
  }, [rootStore.searchReport, match.params.orderId, userType, safeGoToNextPage]);

  const onHomeClickHandler = useCallback(
    () =>
      safeGoToNextPage(
        userType === eAccessControlUserType.Admin ? clientUrls.urlFor.adminOrders() : clientUrls.urlFor.vendorOrders()
      ),
    [userType, safeGoToNextPage]
  );

  const onBackClickHandler = useCallback(
    () => safeGoToNextPage(clientUrls.urlFor.orderPage(userType, match.params.orderId)),
    [userType, match.params.orderId, safeGoToNextPage]
  );

  const onSaveHandler = useCallback(
    () => rootStore.searchReport!.save(match.params.orderId),
    [rootStore.searchReport, match.params.orderId]
  );

  const onYesClickHandler = useCallback(() => {
    rootStore.appData!.setSaveAttempted(true);
    rootStore.searchReport!.getIsFormDataValid().then((isValid) => {
      if (isValid) {
        rootStore
          .searchReport!.save(match.params.orderId)
          .then(goToNextPage)
          .catch((err) => {
            console.error('Save error:', err);
          });
      } else {
        rootStore.appData!.setErrorMessage('Please correct form errors before saving.');
        setShowSaveDialog(false);
      }
    });
  }, [rootStore.appData, rootStore.searchReport, match.params.orderId, goToNextPage]);

  const { appData, notifications, searchReport } = rootStore;
  if (!appData || !notifications || !searchReport) return <PageLoading isLoading />;

  const { errorMessage, saveAttempted } = appData;
  const action = match.params.action;
  const searchReportUiUrl = clientUrls.urlFor.searchReport(userType, match.params.orderId, action);
  const requirementsExceptionsUiUrl = clientUrls.urlFor.searchReportExceptions(userType, match.params.orderId, action);

  return (
    <div className="App">
      <div>
        <WorkflowHeader onBackClick={onBackClickHandler} onHomeClick={onHomeClickHandler} title={getTitle()}>
          <SearchReportHeaderButtons
            showSaveSpinner={notifications!.saveState.fetching}
            showSaveSuccess={notifications!.saveState.success && !errorMessage && !searchReport.formDataChanged}
            showFinalizeSpinner={notifications!.finalizeState.fetching}
            showFinalizeSuccess={notifications!.finalizeState.success && !errorMessage && !searchReport.formDataChanged}
            disableFinalize={!!errorMessage || !appData!.finalizable}
            onSave={onSaveHandler}
            onFinalize={onFinalizeHandler}
          />
        </WorkflowHeader>

        <ReportNavBar
          options={[
            {
              onClick: (to) => safeGoToNextPage(to),
              text: 'Search Report',
              to: searchReportUiUrl,
            },
            {
              onClick: (to) => safeGoToNextPage(to),
              text: 'Requirements & Exceptions',
              to: requirementsExceptionsUiUrl,
            },
          ]}
        />

        <div className="page-content">
          {errorMessage && <ErrorMessage message={errorMessage} />}

          {showSaveDialog && (
            <SaveDialog
              errorMessage={notifications!.saveState.error ? errorMessage : undefined}
              showSaveSpinner={notifications!.saveState.fetching}
              showSaveSuccess={!errorMessage && !searchReport.formDataChanged && notifications!.saveState.success}
              onYesClick={onYesClickHandler}
              onNoClick={goToNextPage}
            />
          )}

          {notifications!.isFetchingAny && (
            <Card>
              <FlexRow>
                <Spinner size={SpinnerSize.regular} />
              </FlexRow>
            </Card>
          )}

          {!notifications!.isFetchingAny && (
            <React.Fragment>
              <Summary
                data={searchReport.formData.summary}
                error={saveAttempted ? searchReport.formErrors : {}}
                onFormChange={searchReport.setPropVal}
              />

              <LegalAndVesting
                data={searchReport.formData.legalAndVesting}
                error={saveAttempted ? searchReport.formErrors : {}}
                onFormChange={searchReport.setPropVal}
                validateFormData={searchReport.validateFormData}
              />

              <PropertyTaxSummary
                data={searchReport.formData.summary.propertyTaxSummary}
                error={saveAttempted ? searchReport.formErrors : {}}
                onFormChange={searchReport.setPropVal}
              />

              <DeedInformation
                data={searchReport.formData.deedInformation}
                error={saveAttempted ? searchReport.formErrors : {}}
                onAddNewItem={searchReport.addNewItem}
                onDeleteItem={searchReport.deleteItem}
                onFormChange={searchReport.setPropVal}
                validateFormData={searchReport.validateFormData}
              />

              <MortgageInformation
                data={searchReport.formData.mortgageInformation}
                error={saveAttempted ? searchReport.formErrors : {}}
                onAddNewItem={searchReport.addNewItem}
                onDeleteItem={searchReport.deleteItem}
                onFormChange={searchReport.setPropVal}
                validateFormData={searchReport.validateFormData}
              />

              <JudgementInformation
                data={searchReport.formData.judgementInformation}
                error={saveAttempted ? searchReport.formErrors : {}}
                onAddNewItem={searchReport.addNewItem}
                onDeleteItem={searchReport.deleteItem}
                onFormChange={searchReport.setPropVal}
                validateFormData={searchReport.validateFormData}
              />

              <BankruptcyInformation
                data={searchReport.formData.bankruptcyInformation || createDefaultBankruptcyData()}
                onFormChange={searchReport.setPropVal}
              />

              {userType === eAccessControlUserType.Admin && order && order.property.state === eUSAState.TX && (
                <TitleEvidence
                  data={searchReport.formData.summary}
                  titleEvidenceFeeType={searchReport.formData.titleEvidenceFeeType || eTitleEvidenceFeeType.fee}
                  error={saveAttempted ? searchReport.formErrors : {}}
                  onFormChange={searchReport.setPropVal}
                  validateFormData={searchReport.validateFormData}
                />
              )}
            </React.Fragment>
          )}
        </div>
      </div>

      <div>
        <Footer title="Save or submit Search Report & Title Commitment">
          <SearchReportHeaderButtons
            showSaveSpinner={notifications!.saveState.fetching}
            showSaveSuccess={notifications!.saveState.success && !errorMessage && !searchReport.formDataChanged}
            showFinalizeSpinner={notifications!.finalizeState.fetching}
            showFinalizeSuccess={notifications!.finalizeState.success && !errorMessage && !searchReport.formDataChanged}
            disableFinalize={!!errorMessage || !appData!.finalizable}
            onSave={onSaveHandler}
            onFinalize={onFinalizeHandler}
          />
        </Footer>

        <Copyright />
      </div>
    </div>
  );
};

export default observer(SearchReport);
